import * as Headless from '@headlessui/react';
import clsx from 'clsx';

export function RadioGroup({
  className,
  ...props
}: { className?: string } & Omit<Headless.RadioGroupProps, 'as' | 'className'>) {
  return (
    <Headless.RadioGroup
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Basic groups
        'tw:space-y-3 [&_[data-slot=label]]:tw:font-normal',
        // With descriptions
        'has-[[data-slot=description]]:tw:space-y-6 [&_[data-slot=label]]:has-[[data-slot=description]]:tw:font-medium'
      )}
    />
  );
}

export function RadioField({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        className,
        // Base layout
        'tw:grid tw:grid-cols-[1.125rem_1fr] tw:items-center tw:gap-x-4 tw:gap-y-1 tw:sm:grid-cols-[1rem_1fr]',
        // Control layout
        '[&>[data-slot=control]]:tw:col-start-1 [&>[data-slot=control]]:tw:row-start-1 [&>[data-slot=control]]:tw:justify-self-center',
        // Label layout
        '[&>[data-slot=label]]:tw:col-start-2 [&>[data-slot=label]]:tw:row-start-1 [&>[data-slot=label]]:tw:justify-self-start',
        // Description layout
        '[&>[data-slot=description]]:tw:col-start-2 [&>[data-slot=description]]:tw:row-start-2',
        // With description
        '[&_[data-slot=label]]:has-[[data-slot=description]]:tw:font-medium'
      )}
    />
  );
}

const base = [
  // Basic layout
  'tw:relative tw:isolate tw:flex tw:size-[1.1875rem] tw:shrink-0 tw:rounded-full tw:sm:size-[1.0625rem]',
  // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
  'tw:before:absolute tw:before:inset-0 tw:before:-z-10 tw:before:rounded-full tw:before:bg-white tw:before:shadow',
  // Background color when checked
  'tw:before:group-data-[checked]:bg-[var(--radio-checked-bg)]',
  // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
  'tw:dark:before:hidden',
  // Background color applied to control in dark mode
  'tw:dark:bg-white/5 tw:dark:group-data-[checked]:bg-[var(--radio-checked-bg)]',
  // Border
  'tw:border tw:border-zinc-950/15 tw:group-data-[checked]:border-transparent tw:group-data-[checked]:group-data-[hover]:border-transparent tw:group-data-[hover]:border-zinc-950/30 tw:group-data-[checked]:bg-[var(--radio-checked-border)]',
  'tw:dark:border-white/15 tw:dark:group-data-[checked]:border-white/5 tw:dark:group-data-[checked]:group-data-[hover]:border-white/5 tw:dark:group-data-[hover]:border-white/30',
  // Inner highlight shadow
  'tw:after:absolute tw:after:inset-0 tw:after:rounded-full tw:after:shadow-[inset_0_1px_theme(colors.white/15%)]',
  'tw:dark:after:-inset-px tw:dark:after:hidden tw:dark:after:rounded-full tw:dark:group-data-[checked]:after:block',
  // Indicator color (light mode)
  'tw:[--radio-indicator:transparent] tw:group-data-[checked]:[--radio-indicator:var(--radio-checked-indicator)] tw:group-data-[checked]:group-data-[hover]:[--radio-indicator:var(--radio-checked-indicator)] tw:group-data-[hover]:[--radio-indicator:theme(colors.zinc.900/10%)]',
  // Indicator color (dark mode)
  'tw:dark:group-data-[checked]:group-data-[hover]:[--radio-indicator:var(--radio-checked-indicator)] tw:dark:group-data-[hover]:[--radio-indicator:theme(colors.zinc.700)]',
  // Focus ring
  'tw:group-data-[focus]:outline tw:group-data-[focus]:outline-2 tw:group-data-[focus]:outline-offset-2 tw:group-data-[focus]:outline-blue-500',
  // Disabled state
  'tw:group-data-[disabled]:opacity-50',
  'tw:group-data-[disabled]:border-zinc-950/25 tw:group-data-[disabled]:bg-zinc-950/5 tw:group-data-[disabled]:[--radio-checked-indicator:theme(colors.zinc.950/50%)] tw:group-data-[disabled]:before:bg-transparent',
  'tw:dark:group-data-[disabled]:border-white/20 tw:dark:group-data-[disabled]:bg-white/[2.5%] tw:dark:group-data-[disabled]:[--radio-checked-indicator:theme(colors.white/50%)] tw:dark:group-data-[disabled]:group-data-[checked]:after:hidden',
];

const colors = {
  'dark/zinc': [
    'tw:[--radio-checked-bg:theme(colors.zinc.900)] tw:[--radio-checked-border:theme(colors.zinc.950/90%)] tw:[--radio-checked-indicator:theme(colors.white)]',
    'tw:dark:[--radio-checked-bg:theme(colors.zinc.600)]',
  ],
  'dark/white': [
    'tw:[--radio-checked-bg:theme(colors.zinc.900)] tw:[--radio-checked-border:theme(colors.zinc.950/90%)] tw:[--radio-checked-indicator:theme(colors.white)]',
    'tw:dark:[--radio-checked-bg:theme(colors.white)] tw:dark:[--radio-checked-border:theme(colors.zinc.950/15%)] tw:dark:[--radio-checked-indicator:theme(colors.zinc.900)]',
  ],
  white:
    'tw:[--radio-checked-bg:theme(colors.white)] tw:[--radio-checked-border:theme(colors.zinc.950/15%)] tw:[--radio-checked-indicator:theme(colors.zinc.900)]',
  dark: 'tw:[--radio-checked-bg:theme(colors.zinc.900)] tw:[--radio-checked-border:theme(colors.zinc.950/90%)] tw:[--radio-checked-indicator:theme(colors.white)]',
  zinc: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.zinc.600)] tw:[--radio-checked-border:theme(colors.zinc.700/90%)]',
  red: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.red.600)] tw:[--radio-checked-border:theme(colors.red.700/90%)]',
  orange:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.orange.500)] tw:[--radio-checked-border:theme(colors.orange.600/90%)]',
  amber:
    'tw:[--radio-checked-bg:theme(colors.amber.400)] tw:[--radio-checked-border:theme(colors.amber.500/80%)] tw:[--radio-checked-indicator:theme(colors.amber.950)]',
  yellow:
    'tw:[--radio-checked-bg:theme(colors.yellow.300)] tw:[--radio-checked-border:theme(colors.yellow.400/80%)] tw:[--radio-checked-indicator:theme(colors.yellow.950)]',
  lime: 'tw:[--radio-checked-bg:theme(colors.lime.300)] tw:[--radio-checked-border:theme(colors.lime.400/80%)] tw:[--radio-checked-indicator:theme(colors.lime.950)]',
  green:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.green.600)] tw:[--radio-checked-border:theme(colors.green.700/90%)]',
  emerald:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.emerald.600)] tw:[--radio-checked-border:theme(colors.emerald.700/90%)]',
  teal: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.teal.600)] tw:[--radio-checked-border:theme(colors.teal.700/90%)]',
  cyan: 'tw:[--radio-checked-bg:theme(colors.cyan.300)] tw:[--radio-checked-border:theme(colors.cyan.400/80%)] tw:[--radio-checked-indicator:theme(colors.cyan.950)]',
  sky: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.sky.500)] tw:[--radio-checked-border:theme(colors.sky.600/80%)]',
  blue: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.blue.600)] tw:[--radio-checked-border:theme(colors.blue.700/90%)]',
  indigo:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.indigo.500)] tw:[--radio-checked-border:theme(colors.indigo.600/90%)]',
  violet:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.violet.500)] tw:[--radio-checked-border:theme(colors.violet.600/90%)]',
  purple:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.purple.500)] tw:[--radio-checked-border:theme(colors.purple.600/90%)]',
  fuchsia:
    'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.fuchsia.500)] tw:[--radio-checked-border:theme(colors.fuchsia.600/90%)]',
  pink: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.pink.500)] tw:[--radio-checked-border:theme(colors.pink.600/90%)]',
  rose: 'tw:[--radio-checked-indicator:theme(colors.white)] tw:[--radio-checked-bg:theme(colors.rose.500)] tw:[--radio-checked-border:theme(colors.rose.600/90%)]',
  brandgreen1:
    'tw:[--radio-checked-indicator:theme(colors.brand-green-1)] tw:[--radio-checked-bg:theme(colors.brand-green-1)] tw:[--radio-checked-border:theme(colors.brand-green-1/90%)]',
  brandgreen:
    'tw:[--radio-checked-indicator:theme(colors.brand-green-0)] tw:[--radio-checked-bg:theme(colors.brand-green-0)] tw:dark:[--radio-checked-bg:theme(colors.brand-green-1)] tw:[--radio-checked-border:theme(colors.brand-green-0/90%)]',
};

type Color = keyof typeof colors;

export function Radio({
  color = 'brandgreen',
  className,
  ...props
}: { color?: Color; className?: string } & Omit<
  Headless.RadioProps,
  'as' | 'className' | 'children'
>) {
  return (
    <Headless.Radio
      data-slot="control"
      {...props}
      className={clsx(className, 'tw:group tw:inline-flex tw:focus:outline-none')}
    >
      <span className={clsx([base, colors[color]])}>
        <span
          className={clsx(
            'tw:size-full tw:rounded-full tw:border-[4.5px] tw:border-transparent tw:bg-[var(--radio-indicator)] tw:bg-clip-padding',
            // Forced colors mode
            'tw:forced-colors:border-[Canvas] tw:forced-colors:group-data-[checked]:border-[Highlight]'
          )}
        />
      </span>
    </Headless.Radio>
  );
}
